import { isDeepStrictEqual } from './equal';

import type {
  ChurchDemoResponse,
  EventResource,
  FollowUpCreate,
  FollowUpPatch,
  VisitorContactRequest,
  VisitorCreate,
  VisitorInput,
  PhotoUpload,
  PrivacyRequestCreate,
  PrivacyResolveRequest,
  StateDto,
} from '@escala/contracts';
import type { AudienceInput } from '@escala/domain/audience';
import type { GenerationOptions, GenerationResult, ProposalAdjustment } from '@escala/domain/generator';
import type { MemberEdit } from '@escala/domain/memberBulk';
import type { EventDraft, EventScope, MinistryDraft } from '@escala/domain/transitions';
import type {
  Attendance,
  DomainState,
  Member,
  RegistrationLink,
  ResponseStatus,
  Role,
  ScheduleEvent,
  Settings,
  VisitorLink,
  VisitorVisit,
  MergeField,
} from '@escala/domain/types';

import { ApiError, resolveApiBaseUrl } from '@/services/http';
import type { Api } from '@/services/api/escala';

/**
 * Sincronização da store com a API.
 *
 * A interface aplica cada ação na hora, com a mesma transição pura de
 * `@escala/domain/transitions` que o servidor usa; esta camada repete a ação na API, em
 * **fila única** (a ordem das ações é a ordem das chamadas), com a versão de cada evento
 * no `If-Match`. Se a API recusar, o aviso mostra o motivo e o estado é recarregado do
 * servidor — a tela nunca fica mostrando algo que não foi gravado.
 */

export interface RemoteHooks {
  /** Mensagem de erro para o aviso. */
  onError: (message: string) => void;
  /** Recarrega o estado inteiro da API. */
  resync: () => void;
}

const GENERATION_POLL_MS = 300;
const GENERATION_TIMEOUT_MS = 60_000;

/** O estado da API no formato da store, com as versões dos eventos à parte. */
export function fromStateDto(dto: StateDto): { state: DomainState; versions: Map<string, number> } {
  const versions = new Map(dto.events.map((e) => [e.id, e.version]));
  const members = dto.members.map((m) => (m.photo ? { ...m, photo: resolvePhotoUrl(m.photo) } : m));
  const registrations = dto.registrations.map((r) =>
    r.photo ? { ...r, photo: resolvePhotoUrl(r.photo) } : r
  );

  return { state: { ...dto, members, registrations, events: dto.events.map(withoutVersion) }, versions };
}

/**
 * A API dá o endereço da foto a partir da raiz (`/api/v1/members/…`, ou
 * `/api/v1/registrations/…` na solicitação de cadastro): resolvido contra a
 * base da API, funciona com o proxy do desenvolvimento e com a API em outra origem.
 */
export function resolvePhotoUrl(photo: string | null): string | null {
  return photo?.startsWith('/') ? new URL(photo, `${resolveApiBaseUrl()}/`).href : photo;
}

export function withoutVersion({ version: _version, ...event }: EventResource): ScheduleEvent {
  return event;
}

/** A mensagem que a API deu (RFC 9457), ou uma genérica. */
export function problemMessage(error: unknown): string {
  if (error instanceof ApiError && typeof error.body === 'object' && error.body !== null) {
    const { detail, title } = error.body as { detail?: unknown; title?: unknown };

    if (typeof detail === 'string') return detail;
    if (typeof title === 'string') return title;
  }

  return 'Não foi possível salvar no servidor. Os dados foram recarregados.';
}

export function createRemote(api: Api, versions: Map<string, number>) {
  let queue: Promise<void> = Promise.resolve();
  // Ligados pela store (`setHooks`) depois de montar: o aviso e a recarga vivem nela.
  let hooks: RemoteHooks = { onError: () => {}, resync: () => {} };
  const runs = new WeakMap<GenerationResult, string>();

  /**
   * Enfileira a chamada; erro avisa e recarrega (as próximas da fila seguem normalmente).
   * Devolve se deu certo, para a tela confirmar só o que o servidor gravou.
   */
  const enqueue = (task: () => Promise<unknown>): Promise<boolean> => {
    const run = queue.then(task).then(
      () => true,
      (error: unknown) => {
        hooks.onError(problemMessage(error));
        hooks.resync();

        return false;
      }
    );

    queue = run.then(() => undefined);

    return run;
  };

  const decidedPhoto = async (
    task: () => Promise<{ member: { photo: string | null } | null }>
  ): Promise<string | null | undefined> => {
    let photo: string | null | undefined;
    const ok = await enqueue(async () => {
      photo = resolvePhotoUrl((await task()).member?.photo ?? null);
    });

    return ok ? photo : undefined;
  };

  const track = (events: readonly EventResource[]) => events.forEach((e) => versions.set(e.id, e.version));
  const trackOne = (result: { event: EventResource }) => track([result.event]);
  const v = (eventId: string) => versions.get(eventId);

  return {
    /** Espera a fila esvaziar (testes, sair). */
    idle: () => queue,

    setHooks(next: RemoteHooks) {
      hooks = next;
    },

    replaceVersions(next: Map<string, number>) {
      versions.clear();
      next.forEach((value, key) => versions.set(key, value));
    },

    assign: (eventId: string, roleId: string, memberId: string, replaceId?: string | null) =>
      enqueue(() =>
        api.assign(eventId, { roleId, memberId, replaceId: replaceId ?? null }, v(eventId)).then(trackOne)
      ),

    remove: (eventId: string, roleId: string, memberId: string) =>
      enqueue(() => api.remove(eventId, roleId, memberId, v(eventId)).then(trackOne)),

    move: (eventId: string, fromRoleId: string, toRoleId: string, memberId: string) =>
      enqueue(() => api.move(eventId, { fromRoleId, toRoleId, memberId }, v(eventId)).then(trackOne)),

    publish: (eventId: string) => enqueue(() => api.publish(eventId, v(eventId)).then(trackOne)),

    /**
     * Desfazer: leva o servidor de `current` a `target`. Escala alterada volta pelo
     * `PUT /slots`; evento apagado ou editado volta inteiro (`PUT /events/:id`); evento
     * criado sai.
     */
    restore: (current: readonly ScheduleEvent[], target: readonly ScheduleEvent[], message: string) =>
      enqueue(async () => {
        const now = new Map(current.map((e) => [e.id, e]));
        const then = new Map(target.map((e) => [e.id, e]));

        for (const event of target) {
          const live = now.get(event.id);

          if (!live) track([await api.restoreEvent(event)]);
          else if (!isDeepStrictEqual(live, event)) {
            const onlySchedule = isDeepStrictEqual(
              // Confirmações e check-in acompanham a escala (o servidor os acerta).
              { ...live, slots: null, published: null, generated: null, responses: null, attendance: null },
              { ...event, slots: null, published: null, generated: null, responses: null, attendance: null }
            );

            if (onlySchedule) {
              const { slots, published, generated } = event;
              const responses = Object.fromEntries(
                Object.entries(event.responses).filter(([, r]) => r.status === 'declined')
              );

              trackOne(
                await api.restoreSlots(
                  event.id,
                  { slots, published, generated, responses },
                  message,
                  v(event.id)
                )
              );
            } else track([await api.restoreEvent(event)]);
          }
        }

        for (const event of current) {
          if (!then.has(event.id)) {
            await api.deleteEvents(event.id, 'one');
            versions.delete(event.id);
          }
        }
      }),

    /** Pede a geração na fila do servidor e espera a proposta. */
    async generate(eventIds: string[], options: GenerationOptions): Promise<GenerationResult> {
      await queue;

      const started = Date.now();
      let run = await api.requestGeneration({
        eventIds,
        options: {
          ministryIds: [...options.ministryIds],
          disabledRoleIds: [...options.disabledRoleIds],
          limit: options.limit,
          allowUnset: options.allowUnset,
          criteria: options.criteria,
          ...(options.excludedMemberIds ? { excludedMemberIds: [...options.excludedMemberIds] } : {}),
          ...(options.needOverrides ? { needOverrides: { ...options.needOverrides } } : {}),
        },
      });

      while (run.status === 'queued' || run.status === 'running') {
        if (Date.now() - started > GENERATION_TIMEOUT_MS) throw new Error('A geração demorou demais.');
        await new Promise((resolve) => setTimeout(resolve, GENERATION_POLL_MS));
        run = await api.generationRun(run.id);
      }

      if (run.status !== 'done' || !run.result) throw new Error(run.error ?? 'A geração falhou.');

      runs.set(run.result, run.id);

      return run.result;
    },

    applyGeneration: (
      result: GenerationResult,
      publish: boolean,
      adjustments: readonly ProposalAdjustment[]
    ) =>
      enqueue(async () => {
        const runId = runs.get(result);

        if (!runId) throw new Error('Esta prévia não veio do servidor. Gere de novo.');

        track((await api.applyGeneration(runId, publish, adjustments)).events);
      }),

    createEvents: (draft: EventDraft, idSeed: string) =>
      enqueue(async () => {
        const { title, color = null, date, time, ministryIds, need, recurrence } = draft;

        track(await api.createEvents({ title, color, date, time, ministryIds, need, recurrence, idSeed }));
      }),

    updateEvents: (draft: EventDraft, choices: { includeScheduled: boolean; includePastEmpty: boolean }) =>
      enqueue(async () => {
        const { eventId, scope, title, color, date, time, ministryIds, need, until } = draft;

        if (!eventId) return;
        track(
          await api.updateEvents(
            eventId,
            {
              scope,
              title,
              ...(color !== undefined ? { color } : {}),
              date,
              time,
              ministryIds,
              need,
              ...(until ? { until } : {}),
              ...choices,
            },
            v(eventId)
          )
        );
      }),

    deleteEvent: (event: ScheduleEvent, scope: EventScope) =>
      enqueue(() => api.deleteEvents(event.id, scope)),

    skipOccurrence: (event: ScheduleEvent, reason: string) =>
      enqueue(async () => {
        track(await api.skipOccurrence(event.id, { reason }));
        versions.delete(event.id);
      }),

    restoreSkipped: (seriesId: string, date: string) =>
      enqueue(async () => track(await api.restoreSkipped(seriesId, { date }))),

    backfill: (seriesId: string, since: string) =>
      enqueue(async () => track(await api.backfill(seriesId, { since }))),

    addOccurrence: (seriesId: string, date: string, time: string) =>
      enqueue(async () => track([await api.addOccurrence(seriesId, { date, time })])),

    served: (eventId: string, roleId: string, memberId: string, served: boolean) =>
      enqueue(() => api.served(eventId, { roleId, memberId, served }, v(eventId)).then(trackOne)),

    copyServed: (eventId: string, roleIds: string[]) =>
      enqueue(() => api.copyServed(eventId, { roleIds }, v(eventId)).then(trackOne)),

    clearServed: (eventId: string, roleIds: string[]) =>
      enqueue(() => api.clearServed(eventId, { roleIds }, v(eventId)).then(trackOne)),

    setRecorded: (eventId: string, recorded: boolean) =>
      enqueue(() => api.setRecorded(eventId, recorded, v(eventId)).then(trackOne)),

    /** Na criação, `roles` são as funções que já nascem com o ministério (modelo). */
    saveMinistry: (draft: MinistryDraft, id: string, roles: readonly Role[] = []) =>
      enqueue(() => {
        const { name, description, color, icon, leaderIds, welcome } = draft;
        const body = {
          name,
          description,
          color,
          icon,
          leaderIds,
          ...(welcome === undefined ? {} : { welcome }),
        };
        const seeds = roles.map((r) => ({
          id: r.id,
          name: r.name,
          icon: r.icon,
          qty: r.qty,
          allowedSex: r.allowedSex,
        }));

        return draft.id
          ? api.updateMinistry(draft.id, body)
          : api.createMinistry({ ...body, id, ...(seeds.length ? { roles: seeds } : {}) });
      }),

    deleteMinistry: (ministryId: string) => enqueue(() => api.deleteMinistry(ministryId)),

    addRole: (ministryId: string, roleId: string) => enqueue(() => api.addRole(ministryId, roleId)),

    updateRole: (roleId: string, patch: Partial<Omit<Role, 'id'>>) =>
      enqueue(() => api.updateRole(roleId, patch)),

    deleteRole: (roleId: string) => enqueue(() => api.deleteRole(roleId)),

    enableInRole: (roleId: string, memberId: string) => enqueue(() => api.enableInRole(roleId, memberId)),

    disableInRole: (roleId: string, memberId: string) => enqueue(() => api.disableInRole(roleId, memberId)),

    // A foto não vai no cadastro: muda só pela rota dela (`setMemberPhoto`).
    saveMember: (member: Member, isNew: boolean) =>
      enqueue(() => {
        const { id, photo: _photo, ...body } = member;

        return isNew ? api.createMember({ id, ...body }) : api.saveMember(id, body);
      }),

    /**
     * O link para completar o cadastro. Espera a fila (o membro recém-importado já gravado)
     * e devolve o link — ou lança o erro da API, que a tela mostra no diálogo.
     */
    createProfileLink: async (memberId: string) => {
      await queue;

      return api.createProfileLink(memberId);
    },

    /** Funções e disponibilidade de vários membros numa requisição (o Editor de Membros). */
    bulkEditMembers: (edits: readonly MemberEdit[]) =>
      enqueue(() => api.bulkEditMembers({ edits: [...edits] })),

    /** A importação já foi validada na tela: cada membro novo entra pelo cadastro. */
    importMembers: (created: readonly Member[]) =>
      enqueue(async () => {
        for (const { id, photo: _photo, ...body } of created) await api.createMember({ id, ...body });
      }),

    /**
     * Envia (ou remove) a foto e devolve o endereço gravado — `undefined` se a API recusou
     * (o aviso mostra o motivo). Não é otimista: a tela espera o envio.
     */
    setMemberPhoto: async (
      memberId: string,
      photo: PhotoUpload | null
    ): Promise<string | null | undefined> => {
      let saved: string | null | undefined;
      const ok = await enqueue(async () => {
        saved = resolvePhotoUrl((await api.setMemberPhoto(memberId, photo)).photo);
      });

      return ok ? saved : undefined;
    },

    respond: (eventId: string, status: ResponseStatus, reason: string) =>
      enqueue(async () => track([await api.respond(eventId, { status, ...(reason ? { reason } : {}) })])),

    undoResponse: (eventId: string, restore: 'pending' | 'confirmed') =>
      enqueue(async () => track([await api.undoResponse(eventId, { restore })])),

    requestSwap: (eventId: string, swapId: string, toId: string) =>
      enqueue(async () => track([await api.requestSwap(eventId, { id: swapId, toId })])),

    cancelSwap: (eventId: string, swapId: string) =>
      enqueue(async () => track([await api.cancelSwap(eventId, swapId)])),

    answerSwap: (eventId: string, swapId: string, accept: boolean) =>
      enqueue(async () => track([await api.answerSwap(eventId, swapId, accept)])),

    decideSwap: (eventId: string, swapId: string, approve: boolean) =>
      enqueue(async () => track([await api.decideSwap(eventId, swapId, approve)])),

    /** Os colegas para a troca (a disponibilidade deles não chega ao Membro: o servidor diz a situação). */
    swapCandidates: (eventId: string) => api.swapCandidates(eventId),

    recordAttendance: (eventId: string, marks: Record<string, Attendance>) =>
      enqueue(() => api.recordAttendance(eventId, { marks }).then(trackOne)),

    nudge: (eventId: string) => enqueue(() => api.nudge(eventId).then(trackOne)),

    recordAudience: (eventId: string, input: AudienceInput) =>
      enqueue(async () => track([await api.recordAudience(eventId, input)])),

    whatsapp: (eventId: string, memberId: string) => enqueue(() => api.whatsapp(eventId, { memberId })),

    setUnavailableDate: (memberId: string, date: string, unavailable: boolean) =>
      enqueue(() => api.setUnavailableDate(memberId, date, unavailable)),

    updateSettings: (patch: Partial<Settings>) => enqueue(() => api.updateSettings(patch)),

    /** Os dados da igreja mudam por inteiro: o estado é recarregado. `null` se a API recusou. */
    loadDemoData: async (): Promise<ChurchDemoResponse | null> => {
      let result: ChurchDemoResponse | null = null;
      const ok = await enqueue(async () => {
        result = await api.loadDemoData();
        hooks.resync();
      });

      return ok ? result : null;
    },

    clearChurchData: () =>
      enqueue(async () => {
        await api.clearChurchData();
        hooks.resync();
      }),

    markRead: (ids: string[]) => enqueue(() => api.markRead(ids)),

    hideOnboarding: () => enqueue(() => api.setOnboarding(true)),

    /**
     * Aprova ou vincula e devolve a foto do membro como ficou no servidor — a da solicitação
     * ganha outro endereço ao passar para o membro (`undefined` se a API recusou).
     */
    approveRegistration: (registrationId: string, memberId: string) =>
      decidedPhoto(() => api.approveRegistration(registrationId, memberId)),

    mergeRegistration: (registrationId: string, memberId: string, fields: MergeField[]) =>
      decidedPhoto(() => api.mergeRegistration(registrationId, memberId, fields)),

    rejectRegistration: (registrationId: string, reason: string) =>
      enqueue(() => api.rejectRegistration(registrationId, reason)),

    setRegistrationLinkActive: (active: boolean) => enqueue(() => api.setRegistrationLinkActive(active)),

    submitPrivacyRequest: (body: PrivacyRequestCreate) => enqueue(() => api.submitPrivacyRequest(body)),

    createVisitor: (body: VisitorCreate) => enqueue(() => api.createVisitor(body)),
    updateVisitor: (visitorId: string, body: VisitorInput) =>
      enqueue(() => api.updateVisitor(visitorId, body)),
    anonymizeVisitor: (visitorId: string) => enqueue(() => api.anonymizeVisitor(visitorId)),
    recordVisitorVisit: (visitorId: string, visit: VisitorVisit) =>
      enqueue(() => api.recordVisitorVisit(visitorId, visit)),
    moveVisitor: (visitorId: string, stage: string) => enqueue(() => api.moveVisitor(visitorId, stage)),
    assignVisitorOwner: (visitorId: string, ownerId: string | null) =>
      enqueue(() => api.assignVisitorOwner(visitorId, ownerId)),
    addVisitorFollowUp: (visitorId: string, body: FollowUpCreate) =>
      enqueue(() => api.addVisitorFollowUp(visitorId, body)),
    updateVisitorFollowUp: (visitorId: string, followUpId: string, body: FollowUpPatch) =>
      enqueue(() => api.updateVisitorFollowUp(visitorId, followUpId, body)),
    logVisitorContact: (visitorId: string, body: VisitorContactRequest) =>
      enqueue(() => api.logVisitorContact(visitorId, body)),
    referVisitor: (visitorId: string, registrationId: string) =>
      enqueue(() => api.referVisitor(visitorId, registrationId)),
    linkVisitorRegistration: (visitorId: string, registrationId: string) =>
      enqueue(() => api.linkVisitorRegistration(visitorId, registrationId)),
    setVisitorLinkActive: (active: boolean) => enqueue(() => api.setVisitorLinkActive(active)),

    /** O token novo é do servidor: espera a fila e devolve o link (ou `null`, se falhou). */
    async rotateVisitorLink(): Promise<VisitorLink | null> {
      let link: VisitorLink | null = null;

      await enqueue(async () => {
        link = await api.rotateVisitorLink();
      });

      return link;
    },

    /** Inativar tira a pessoa das escalas no servidor (com versões novas): recarrega tudo. */
    setMemberInactive: (memberId: string, inactive: boolean) =>
      enqueue(async () => {
        await api.setMemberInactive(memberId, inactive);
        if (inactive) hooks.resync();
      }),

    /** A anonimização muda membros e escalas no servidor (com versões novas): recarrega tudo. */
    resolvePrivacyRequest: (requestId: string, body: PrivacyResolveRequest) =>
      enqueue(async () => {
        const result = await api.resolvePrivacyRequest(requestId, body);

        if (result.anonymizedMemberId) hooks.resync();
      }),

    /** O token novo é do servidor: espera a fila e devolve o link (ou `null`, se falhou). */
    async rotateRegistrationLink(): Promise<RegistrationLink | null> {
      let link: RegistrationLink | null = null;

      await enqueue(async () => {
        link = await api.rotateRegistrationLink();
      });

      return link;
    },
  };
}

export type Remote = ReturnType<typeof createRemote>;
